import React, { useState, useEffect } from 'react'
import {
    Table,
    TableCell,
    TableHead,
    TableContainer,
    TableRow,
    TableBody,
    TablePagination,
    TableSortLabel,
    CircularProgress,
    Typography,
    Chip,
    Popover,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
} from '@material-ui/core'
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined'
import FlagIcon from '@material-ui/icons/Flag'
import { useTheme } from '@material-ui/core/styles'
import { Container } from '../../Container'
import { LoriConversationAnalysisController } from '../../../hooks/useLoriConversationAnalysis'
import {
    ConversationAnalyticsItem,
    ConversationProblem,
    ProblemCategory,
    INTENT_OPTIONS,
    formatLabel,
} from './analysisTypes'
import { ProblemFlagPopover } from './ProblemFlagPopover'
import { Topic } from '../../../hooks/useTopics'

function resolveTopicName(
    topicValue: string | number | null,
    topics: Topic[],
): string | null {
    if (topicValue == null) return null
    const numId =
        typeof topicValue === 'number' ? topicValue : parseInt(topicValue, 10)
    if (!isNaN(numId)) {
        const match = topics.find((t) => t.id === numId)
        if (match) return formatLabel(match.name)
    }
    const matchByName = topics.find((t) => t.name === topicValue)
    if (matchByName) return formatLabel(matchByName.name)
    return formatLabel(String(topicValue))
}

interface ClassificationPopoverState {
    anchorEl: HTMLElement
    convoId: number
    field: 'topic' | 'intent'
}

interface Props {
    analyticsController: LoriConversationAnalysisController
    selectedConversation: ConversationAnalyticsItem | null
    onSelectConversation: (c: ConversationAnalyticsItem) => void
    topics: Topic[]
    problemsByConversationId: Map<number, ConversationProblem>
    onCreateProblem: (data: {
        conversation: number
        category: ProblemCategory
        notes?: string
    }) => void
    onUpdateProblem: (data: {
        id: number
        category: ProblemCategory
        notes?: string
    }) => void
    onDeleteProblem: (id: number) => void
    problemSaving?: boolean
}

export const ConversationListView = ({
    analyticsController,
    selectedConversation,
    onSelectConversation,
    topics,
    problemsByConversationId,
    onCreateProblem,
    onUpdateProblem,
    onDeleteProblem,
    problemSaving,
}: Props) => {
    const theme = useTheme()
    const {
        conversations,
        conversationsLoading,
        conversationsFetching,
        listParams,
        updateListParams,
        updateConversation,
    } = analyticsController

    const [popover, setPopover] = useState<ClassificationPopoverState | null>(
        null,
    )
    const [flagAnchor, setFlagAnchor] = useState<HTMLElement | null>(null)

    const page = listParams.page - 1
    const rowsPerPage = listParams.pageSize
    const orderBy = listParams.orderBy as 'message_count' | 'created_date'
    const order = listParams.order

    useEffect(() => {
        if (
            conversations?.results &&
            conversations.results.length > 0 &&
            !selectedConversation
        ) {
            onSelectConversation(conversations.results[0])
        }
    }, [conversations])

    const handleSort = (property: 'message_count' | 'created_date') => {
        const isAsc = orderBy === property && order === 'asc'
        updateListParams({
            orderBy: property,
            order: isAsc ? 'desc' : 'asc',
        })
    }

    const getPositiveFeedbackCount = (c: ConversationAnalyticsItem) =>
        c.chat_messages.filter((m) => m.user_feedback?.feedback === true).length

    const getNegativeFeedbackCount = (c: ConversationAnalyticsItem) =>
        c.chat_messages.filter((m) => m.user_feedback?.feedback === false)
            .length

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            {conversationsLoading || !conversations ? (
                <Container
                    style={{
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress />
                </Container>
            ) : (
                <>
                    <Container
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                        }}
                    >
                        <TableContainer style={{ padding: theme.spacing(1) }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Workspace</TableCell>
                                        <TableCell>Organization</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Topic</TableCell>
                                        <TableCell>Intent</TableCell>
                                        <TableCell align="center">
                                            Problem
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={
                                                    orderBy === 'created_date'
                                                }
                                                direction={
                                                    orderBy === 'created_date'
                                                        ? order
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort('created_date')
                                                }
                                            >
                                                Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={
                                                    orderBy === 'message_count'
                                                }
                                                direction={
                                                    orderBy === 'message_count'
                                                        ? order
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort('message_count')
                                                }
                                            >
                                                Msgs
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            Feedback
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {conversations.results.map((convo) => (
                                        <TableRow
                                            key={convo.id}
                                            onClick={() =>
                                                onSelectConversation(convo)
                                            }
                                            selected={
                                                selectedConversation?.id ===
                                                convo.id
                                            }
                                            hover
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>
                                                {convo.company.name}
                                            </TableCell>
                                            <TableCell>
                                                {
                                                    convo.company.organization
                                                        .name
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {convo.user.name}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        convo.topic_display ??
                                                        resolveTopicName(
                                                            convo.topic,
                                                            topics,
                                                        ) ??
                                                        '—'
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    clickable
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setPopover({
                                                            anchorEl:
                                                                e.currentTarget,
                                                            convoId: convo.id,
                                                            field: 'topic',
                                                        })
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        convo.intent_display ??
                                                        '—'
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    clickable
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setPopover({
                                                            anchorEl:
                                                                e.currentTarget,
                                                            convoId: convo.id,
                                                            field: 'intent',
                                                        })
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {(() => {
                                                    const isFlagged = problemsByConversationId.has(
                                                        convo.id,
                                                    )
                                                    return (
                                                        <Tooltip
                                                            title={
                                                                isFlagged
                                                                    ? 'Edit Problem'
                                                                    : 'Flag as Problem'
                                                            }
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation()
                                                                    onSelectConversation(
                                                                        convo,
                                                                    )
                                                                    setFlagAnchor(
                                                                        e.currentTarget,
                                                                    )
                                                                }}
                                                            >
                                                                {isFlagged ? (
                                                                    <FlagIcon
                                                                        style={{
                                                                            color:
                                                                                theme
                                                                                    .palette
                                                                                    .error
                                                                                    .main,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <FlagOutlinedIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                {new Date(
                                                    convo.created_date,
                                                ).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                {convo.chat_messages.length}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Container
                                                    style={{
                                                        flexDirection: 'row',
                                                        justifyContent:
                                                            'flex-end',
                                                        gap: 4,
                                                    }}
                                                >
                                                    {getPositiveFeedbackCount(
                                                        convo,
                                                    ) > 0 && (
                                                        <Typography
                                                            variant="caption"
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .success
                                                                        ?.main ??
                                                                    '#4caf50',
                                                            }}
                                                        >
                                                            +
                                                            {getPositiveFeedbackCount(
                                                                convo,
                                                            )}
                                                        </Typography>
                                                    )}
                                                    {getNegativeFeedbackCount(
                                                        convo,
                                                    ) > 0 && (
                                                        <Typography
                                                            variant="caption"
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .error
                                                                        .main,
                                                            }}
                                                        >
                                                            -
                                                            {getNegativeFeedbackCount(
                                                                convo,
                                                            )}
                                                        </Typography>
                                                    )}
                                                </Container>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Popover
                            open={Boolean(popover)}
                            anchorEl={popover?.anchorEl ?? null}
                            onClose={() => setPopover(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <List
                                dense
                                style={{
                                    maxHeight: 300,
                                    overflow: 'auto',
                                    minWidth: 180,
                                }}
                            >
                                {popover?.field === 'topic' &&
                                    topics.map((t) => (
                                        <ListItem
                                            key={t.id}
                                            button
                                            selected={(() => {
                                                const convo = conversations?.results.find(
                                                    (c) =>
                                                        c.id ===
                                                        popover.convoId,
                                                )
                                                if (!convo?.topic) return false
                                                const numId = parseInt(
                                                    String(convo.topic),
                                                    10,
                                                )
                                                return !isNaN(numId)
                                                    ? numId === t.id
                                                    : convo.topic === t.name
                                            })()}
                                            onClick={() => {
                                                updateConversation({
                                                    id: popover.convoId,
                                                    topic: t.id,
                                                })
                                                setPopover(null)
                                            }}
                                        >
                                            <ListItemText
                                                primary={formatLabel(t.name)}
                                            />
                                        </ListItem>
                                    ))}
                                {popover?.field === 'intent' &&
                                    INTENT_OPTIONS.map((opt) => (
                                        <ListItem
                                            key={opt.value}
                                            button
                                            selected={
                                                conversations?.results.find(
                                                    (c) =>
                                                        c.id ===
                                                        popover.convoId,
                                                )?.intent === opt.value
                                            }
                                            onClick={() => {
                                                updateConversation({
                                                    id: popover.convoId,
                                                    intent: opt.value,
                                                })
                                                setPopover(null)
                                            }}
                                        >
                                            <ListItemText primary={opt.label} />
                                        </ListItem>
                                    ))}
                            </List>
                        </Popover>

                        {selectedConversation && (
                            <ProblemFlagPopover
                                anchorEl={flagAnchor}
                                onClose={() => setFlagAnchor(null)}
                                conversationId={selectedConversation.id}
                                existingProblem={
                                    problemsByConversationId.get(
                                        selectedConversation.id,
                                    ) ?? null
                                }
                                onCreate={onCreateProblem}
                                onUpdate={onUpdateProblem}
                                onDelete={onDeleteProblem}
                                saving={problemSaving}
                            />
                        )}
                    </Container>

                    <Container
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <TablePagination
                            component={'div' as React.ElementType}
                            count={conversations.count}
                            page={page}
                            onChangePage={(_, newPage) =>
                                updateListParams({ page: newPage + 1 })
                            }
                            rowsPerPage={rowsPerPage}
                            onChangeRowsPerPage={(e) => {
                                updateListParams({
                                    pageSize: parseInt(e.target.value, 10),
                                    page: 1,
                                })
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </Container>
                </>
            )}
        </Container>
    )
}
